/* Hallmark · pre-emit critique: P4 H5 E4 S5 R5 V4 */
/* Hallmark · macrostructure: Workbench · tone: operational-editorial · anchor hue: heritage-green */
import { useDeferredValue, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ClipboardPlus,
  Copy,
  FileUp,
  Layers,
  Loader2,
  Plus,
  Search,
  Settings2,
} from 'lucide-react';
import { Button } from '@lms/ui';
import type { Program, QuestionBankQuestion, QuestionType } from '@lms/shared/types';
import { usePrograms, useProgram } from '../hooks/usePrograms';
import { useImportBankQuestionsToEvaluation, useQuestionBank } from '../hooks/useQuestionBank';
import { useNotificationStore } from '../store/notifications';
import { getProgramSubjects, getQuestionTypeLabel } from '../components/question-bank-utils';
import { QuestionBankEditorDialog } from '../components/QuestionBankEditorDialog';
import { QuestionBankDocumentImportDialog } from '../components/QuestionBankDocumentImportDialog';
import { QuestionBankCopyDialog } from '../components/QuestionBankCopyDialog';

const PAGE_SIZE = 30;

interface CatalogOption {
  id: string;
  label: string;
}

function getProgramOption(program: Program): CatalogOption {
  return {
    id: program.id,
    label: `${program.code} · ${program.name}`,
  };
}

export function QuestionBank() {
  const [searchParams] = useSearchParams();
  const targetEvaluationId = searchParams.get('evaluationId') ?? '';
  const { data: programsData, isLoading: programsLoading } = usePrograms({ limit: 100 });
  const programs = (programsData?.items ?? []) as Program[];
  const [programId, setProgramId] = useState('');
  const [programSearch, setProgramSearch] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [subjectSearch, setSubjectSearch] = useState('');
  const [type, setType] = useState<QuestionType | ''>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [expandedId, setExpandedId] = useState('');
  const [editingQuestion, setEditingQuestion] = useState<QuestionBankQuestion | undefined>();
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isDocumentImportOpen, setIsDocumentImportOpen] = useState(false);
  const [isCopyOpen, setIsCopyOpen] = useState(false);
  const { addNotification } = useNotificationStore();
  const deferredProgramSearch = useDeferredValue(programSearch.trim());
  const { data: programSuggestionsData } = usePrograms({
    limit: 100,
    search: deferredProgramSearch || undefined,
  });
  const programSuggestions = (programSuggestionsData?.items ?? []) as Program[];
  const programOptions = useMemo(
    () => programSuggestions.map(getProgramOption),
    [programSuggestions]
  );
  const { data: selectedProgram } = useProgram(programId);
  const subjects = useMemo(() => getProgramSubjects(selectedProgram), [selectedProgram]);
  const subjectOptions = useMemo<CatalogOption[]>(
    () =>
      subjects.map(subject => ({
        id: subject.id,
        label: subject.code ? `${subject.code} · ${subject.name}` : subject.name,
      })),
    [subjects]
  );
  const filters = useMemo(
    () => ({
      programId: programId || undefined,
      subjectId: subjectId || undefined,
      type: type || undefined,
      search: search.trim() || undefined,
      page,
      limit: PAGE_SIZE,
    }),
    [page, programId, search, subjectId, type]
  );
  const { data, isLoading, error, refetch } = useQuestionBank(filters);
  const importToEvaluation = useImportBankQuestionsToEvaluation();
  const items = data?.items ?? [];
  const allPageSelected = items.length > 0 && items.every(item => selectedIds.includes(item.id));

  const updateProgram = (nextProgramId: string) => {
    setProgramId(nextProgramId);
    setSubjectId('');
    setSubjectSearch('');
    setPage(1);
    setSelectedIds([]);
  };

  const updateProgramSearch = (value: string) => {
    setProgramSearch(value);
    const selectedOption = programOptions.find(option => option.label === value);
    updateProgram(selectedOption?.id ?? '');
  };

  const updateSubjectSearch = (value: string) => {
    setSubjectSearch(value);
    const selectedOption = subjectOptions.find(option => option.label === value);
    setSubjectId(selectedOption?.id ?? '');
    setPage(1);
  };

  const toggleSelection = (id: string) => {
    setSelectedIds(current =>
      current.includes(id) ? current.filter(item => item !== id) : [...current, id]
    );
  };

  const togglePageSelection = () => {
    setSelectedIds(current => {
      if (allPageSelected) return current.filter(id => !items.some(item => item.id === id));
      return [...new Set([...current, ...items.map(item => item.id)])];
    });
  };

  const addSelectedToEvaluation = async () => {
    if (!targetEvaluationId || !selectedIds.length) return;
    try {
      const created = await importToEvaluation.mutateAsync({
        evaluationId: targetEvaluationId,
        questionIds: selectedIds,
      });
      addNotification({
        type: 'success',
        title: 'Soal ditambahkan ke evaluasi',
        message: `${created.length} soal disalin sebagai snapshot evaluasi.`,
      });
      setSelectedIds([]);
    } catch (caughtError) {
      addNotification({
        type: 'error',
        title: 'Soal belum dapat ditambahkan',
        message: caughtError instanceof Error ? caughtError.message : 'Coba kembali.',
      });
    }
  };

  return (
    <div className="space-y-8 pb-12 animate-fadeIn">
      <header className="flex flex-col gap-6 rounded-3xl bg-surface-container-low p-6 sm:p-8 xl:grid xl:grid-cols-[minmax(0,1fr)_auto] xl:items-end">
        <div className="min-w-0">
          <Link
            to={targetEvaluationId ? `/evaluations/${targetEvaluationId}` : '/evaluations'}
            className="inline-flex items-center gap-2 text-xs font-label font-bold uppercase tracking-widest text-on-surface-variant transition-colors hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
          >
            <ArrowLeft size={14} /> {targetEvaluationId ? 'Kembali ke evaluasi' : 'Evaluasi'}
          </Link>
          <div className="mt-5 flex items-start gap-4">
            <div className="grid size-12 shrink-0 place-items-center rounded-2xl bg-primary text-on-primary shadow-level1">
              <Layers size={23} />
            </div>
            <div className="min-w-0">
              <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.16em] text-primary">
                Repositori kurikulum
              </p>
              <h1 className="mt-1 break-words font-headline text-3xl font-extrabold tracking-tight text-on-surface sm:text-4xl">
                Bank Soal Bersama
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-on-surface-variant">
                Kelola soal lintas program berdasarkan mata pelajaran, pratinjau sebelum digunakan,
                lalu pakai seluruh atau sebagian soal pada evaluasi.
              </p>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row xl:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsDocumentImportOpen(true)}
            className="h-11 rounded-xl px-4 text-xs font-bold active:scale-95"
          >
            <FileUp size={16} /> Impor dokumen
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => {
              setEditingQuestion(undefined);
              setIsEditorOpen(true);
            }}
            className="h-11 rounded-xl px-4 text-xs font-bold active:scale-95"
          >
            <Plus size={16} /> Tambah soal
          </Button>
        </div>
      </header>

      {targetEvaluationId && (
        <section className="flex flex-col gap-4 rounded-2xl bg-primary/10 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <ClipboardPlus size={20} className="mt-0.5 shrink-0 text-primary" />
            <div>
              <h2 className="m-0 text-sm font-headline font-bold text-on-surface">
                Mode pilih untuk evaluasi
              </h2>
              <p className="mt-1 text-xs leading-relaxed text-on-surface-variant">
                Soal yang dipilih akan disalin sebagai snapshot; pembaruan bank tidak mengubah ujian
                yang sudah berjalan.
              </p>
            </div>
          </div>
          <Button
            type="button"
            variant="primary"
            onClick={() => void addSelectedToEvaluation()}
            disabled={!selectedIds.length || importToEvaluation.isPending}
            className="h-11 shrink-0 rounded-xl px-4 text-xs font-bold active:scale-95"
          >
            {importToEvaluation.isPending ? 'Menambahkan…' : `Tambahkan ${selectedIds.length} soal`}
          </Button>
        </section>
      )}

      <section className="rounded-3xl bg-surface-container-low p-4 sm:p-6">
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(16rem,0.9fr)_minmax(15rem,0.8fr)_11rem]">
          <div>
            <label
              htmlFor="bank-search"
              className="mb-2 block text-xs font-label font-bold text-on-surface"
            >
              Cari soal
            </label>
            <div className="relative">
              <Search
                size={16}
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant"
              />
              <input
                id="bank-search"
                value={search}
                onChange={event => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                placeholder="Cari teks pertanyaan…"
                className="w-full rounded-xl bg-surface-container-lowest py-3 pl-11 pr-4 text-sm text-on-surface outline-none transition focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="bank-filter-program"
              className="mb-2 block text-xs font-label font-bold text-on-surface"
            >
              Program
            </label>
            <input
              id="bank-filter-program"
              list="bank-program-suggestions"
              value={programSearch}
              onChange={event => updateProgramSearch(event.target.value)}
              placeholder="Cari kode atau nama program"
              className="w-full rounded-xl bg-surface-container-lowest px-3 py-3 text-sm text-on-surface outline-none transition focus:ring-2 focus:ring-primary/20"
            />
            <datalist id="bank-program-suggestions">
              {programOptions.map(option => (
                <option key={option.id} value={option.label} />
              ))}
            </datalist>
            <p className="mt-2 text-[11px] leading-relaxed text-on-surface-variant">
              Ketik kode atau nama, lalu pilih saran. Program VPS membaca bank soal legacy dengan
              kode kurikulum sama.
            </p>
          </div>
          <div>
            <label
              htmlFor="bank-filter-subject"
              className="mb-2 block text-xs font-label font-bold text-on-surface"
            >
              Mata pelajaran
            </label>
            <input
              id="bank-filter-subject"
              list="bank-subject-suggestions"
              value={subjectSearch}
              onChange={event => updateSubjectSearch(event.target.value)}
              disabled={!programId}
              placeholder={programId ? 'Semua mata pelajaran' : 'Pilih program terlebih dahulu'}
              className="w-full rounded-xl bg-surface-container-lowest px-3 py-3 text-sm text-on-surface outline-none transition focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <datalist id="bank-subject-suggestions">
              {subjectOptions.map(option => (
                <option key={option.id} value={option.label} />
              ))}
            </datalist>
            {programId && subjects.length === 0 && (
              <p className="mt-2 text-[11px] leading-relaxed text-on-surface-variant">
                Bank legacy ini diklasifikasikan pada tingkat program.
              </p>
            )}
          </div>
          <div>
            <label
              htmlFor="bank-filter-type"
              className="mb-2 block text-xs font-label font-bold text-on-surface"
            >
              Tipe
            </label>
            <select
              id="bank-filter-type"
              value={type}
              onChange={event => {
                setType(event.target.value as QuestionType | '');
                setPage(1);
              }}
              className="w-full rounded-xl bg-surface-container-lowest px-3 py-3 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="">Semua tipe</option>
              <option value="MULTIPLE_CHOICE">Pilihan Ganda</option>
              <option value="TRUE_FALSE">Benar / Salah</option>
              <option value="ESSAY">Esai</option>
            </select>
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-3xl bg-surface-container-lowest shadow-level1">
        <div className="flex flex-col gap-4 bg-surface-container-low px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={togglePageSelection}
              aria-label={
                allPageSelected
                  ? 'Batalkan semua soal pada halaman'
                  : 'Pilih semua soal pada halaman'
              }
              className="grid size-9 place-items-center rounded-lg bg-surface-container-lowest text-primary transition-colors hover:bg-surface-container-high focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary active:scale-95"
            >
              <CheckCircle2 size={17} className={allPageSelected ? 'opacity-100' : 'opacity-35'} />
            </button>
            <div>
              <h2 className="m-0 text-sm font-headline font-bold text-on-surface">Katalog soal</h2>
              <p className="mt-1 text-xs text-on-surface-variant">
                {data?.meta.total ?? 0} soal ditemukan · {selectedIds.length} dipilih
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsCopyOpen(true)}
              disabled={!selectedIds.length}
              className="h-9 rounded-lg px-3 text-xs font-bold active:scale-95"
            >
              <Copy size={15} /> Salin kategori
            </Button>
          </div>
        </div>

        {isLoading || programsLoading ? (
          <div className="flex min-h-72 items-center justify-center">
            <Loader2 className="size-7 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="p-8 text-sm text-error">
            {error instanceof Error ? error.message : 'Bank soal belum dapat dimuat.'}
          </div>
        ) : items.length === 0 ? (
          <div className="flex min-h-72 flex-col items-center justify-center p-8 text-center">
            <BookOpen size={34} className="text-on-surface-variant/40" />
            <h3 className="mt-4 font-headline text-lg font-bold text-on-surface">
              Belum ada soal pada kategori ini
            </h3>
            <p className="mt-2 max-w-md text-sm text-on-surface-variant">
              Tambah soal pertama atau impor dokumen untuk mulai membangun bank bersama.
            </p>
          </div>
        ) : (
          <div className="space-y-3 p-4 sm:p-6">
            {items.map(item => (
              <QuestionBankRow
                key={item.id}
                item={item}
                selected={selectedIds.includes(item.id)}
                expanded={expandedId === item.id}
                onToggle={() => toggleSelection(item.id)}
                onExpand={() => setExpandedId(current => (current === item.id ? '' : item.id))}
                onEdit={() => {
                  setEditingQuestion(item);
                  setIsEditorOpen(true);
                }}
              />
            ))}
          </div>
        )}

        {data && data.meta.totalPages > 1 && (
          <div className="flex items-center justify-between bg-surface-container-low px-5 py-4 text-xs text-on-surface-variant sm:px-6">
            <span>
              Halaman {data.meta.page} dari {data.meta.totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setPage(current => Math.max(1, current - 1))}
                disabled={page <= 1}
                className="h-9 rounded-lg px-3 text-xs font-bold active:scale-95"
              >
                Sebelumnya
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setPage(current => Math.min(data.meta.totalPages, current + 1))}
                disabled={page >= data.meta.totalPages}
                className="h-9 rounded-lg px-3 text-xs font-bold active:scale-95"
              >
                Berikutnya
              </Button>
            </div>
          </div>
        )}
      </section>

      {isEditorOpen && (
        <QuestionBankEditorDialog
          programs={programs}
          question={editingQuestion}
          onClose={() => setIsEditorOpen(false)}
          onSaved={() => void refetch()}
        />
      )}
      {isDocumentImportOpen && (
        <QuestionBankDocumentImportDialog
          programs={programs}
          onClose={() => setIsDocumentImportOpen(false)}
          onImported={() => void refetch()}
        />
      )}
      {isCopyOpen && (
        <QuestionBankCopyDialog
          programs={programs}
          questionIds={selectedIds}
          onClose={() => setIsCopyOpen(false)}
          onCopied={() => {
            setSelectedIds([]);
            void refetch();
          }}
        />
      )}
    </div>
  );
}

function QuestionBankRow({
  item,
  selected,
  expanded,
  onToggle,
  onExpand,
  onEdit,
}: {
  item: QuestionBankQuestion;
  selected: boolean;
  expanded: boolean;
  onToggle: () => void;
  onExpand: () => void;
  onEdit: () => void;
}) {
  return (
    <article
      className={`rounded-2xl p-4 transition-colors sm:p-5 ${selected ? 'bg-primary/10' : 'bg-surface-container-low hover:bg-surface-container'}`}
    >
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          aria-label={`Pilih soal: ${item.question}`}
          className="mt-1 size-4 shrink-0 accent-primary"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 text-[10px] font-label font-bold uppercase tracking-wider">
                <span className="rounded-full bg-primary/10 px-2.5 py-1 text-primary">
                  {getQuestionTypeLabel(item.type)}
                </span>
                <span className="rounded-full bg-surface-container-high px-2.5 py-1 text-on-surface-variant">
                  {item.points} poin
                </span>
                <span className="text-on-surface-variant">{item.program?.name ?? 'Program'}</span>
                {item.subject?.name && (
                  <span className="text-on-surface-variant">/ {item.subject.name}</span>
                )}
              </div>
              <h3 className="mt-3 break-words font-body text-sm font-bold leading-relaxed text-on-surface">
                {item.question}
              </h3>
              <p className="mt-2 text-xs text-on-surface-variant">
                Dipakai pada {item._count?.usages ?? 0} evaluasi
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                onClick={onExpand}
                aria-expanded={expanded}
                className="inline-flex h-9 items-center gap-1 rounded-lg bg-surface-container-lowest px-3 text-xs font-bold text-primary transition-colors hover:bg-surface-container-high focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary active:scale-95"
              >
                Pratinjau {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
              </button>
              <button
                type="button"
                onClick={onEdit}
                className="inline-flex h-9 items-center gap-1 rounded-lg bg-surface-container-lowest px-3 text-xs font-bold text-on-surface transition-colors hover:bg-surface-container-high focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary active:scale-95"
              >
                <Settings2 size={15} /> Ubah
              </button>
            </div>
          </div>
          {expanded && (
            <div className="mt-4 rounded-xl bg-surface-container-lowest p-4">
              <p className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
                Pratinjau jawaban
              </p>
              {item.options.length ? (
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {item.options.map(option => (
                    <div
                      key={option.id}
                      className={`rounded-xl px-3 py-2 text-xs ${option.isCorrect ? 'bg-success-container font-semibold text-on-success-container' : 'bg-surface-container-low text-on-surface-variant'}`}
                    >
                      {option.isCorrect ? '✓ ' : '○ '}
                      {option.text}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-3 text-xs text-on-surface-variant">
                  Jawaban esai diperiksa oleh pengajar.
                </p>
              )}
              {item.explanation && (
                <p className="mt-4 rounded-xl bg-primary/5 p-3 text-xs leading-relaxed text-on-surface-variant">
                  <span className="font-bold text-primary">Pembahasan: </span>
                  {item.explanation}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
